import { useMediaQuery, Text } from '@ltvco/refresh-lib/theme';
import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';

import {
  AnimatedButtonContainer,
  AnimatedInputButtonContainer,
  AnimatedInputContainer,
  AnimatedSendButton,
  ArrowUpwardIcon,
  AutosuggestionPrompts,
  ExpandedPlaceholderContainer,
  ExpandedPlaceholderText,
  SendButton,
  StyledInputChat,
  StyledInputChatWithExpandedPlaceholder,
} from 'components/ChatBV/components';
import { PLACEHOLDER_COLLECTION } from 'components/ChatBV/constants';
import { useChatForm } from 'components/ChatBV/hooks/useChatForm';
import { getTrailingSpaceForType } from 'components/ChatBV/utils/generalUtils';

import { ClickHereTooltip } from './ClickHereTooltip';

interface ChatInputProps {
  onSendMessage?: (message: string) => void;
  isReportOpen?: boolean;
  isLanding?: boolean;
  isAnimating?: boolean;
  animationPhase?: 'center' | 'moving-up' | 'moving-left' | 'header';
  onChipClick?: (chipTitle: string) => void;
  isLoading?: boolean;
}

export interface ChatInputRef {
  handleChipClick: (type: string) => void;
}

export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(
  (
    {
      onSendMessage,
      isReportOpen,
      isLanding = false,
      isAnimating = false,
      animationPhase = 'center',
      isLoading = false,
    },
    ref
  ) => {
    const { inputValue, handleInputChange, handleKeyPress, handleSendMessage } =
      useChatForm(onSendMessage ?? (() => {}));

    const hasInputValue = inputValue.trim().length > 0;
    const isMobile = useMediaQuery('(max-width: 1024px)');
    const [termsPosition, setTermsPosition] = useState({
      top: 0,
      left: 0,
      width: 0,
    });
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [currentPlaceholder, setCurrentPlaceholder] = useState(
      'What can I help you find?'
    );
    const [expandedPlaceholder, setExpandedPlaceholder] = useState('');
    const [textWidth, setTextWidth] = useState(0);
    const [isFromClick, setIsFromClick] = useState(false);
    const inputRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const inputElementRef = useRef<HTMLTextAreaElement>(null);

    //We will need to check if this is needed
    // Calculate text width for placeholder positioning
    useEffect(() => {
      if (inputValue) {
        // Use canvas for precise text measurement
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
          context.font =
            '16px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          const width = context.measureText(inputValue).width;
          setTextWidth(width);
        } else {
          const approximateWidth = inputValue.length * 8.5;
          setTextWidth(approximateWidth);
        }
      } else {
        setTextWidth(0);
      }
    }, [inputValue]);

    // Calculate the position to move the input to the chat page input position
    useEffect(() => {
      if (isLanding && isAnimating && animationPhase === 'moving-up') {
        const inputElement = inputRef.current;

        if (inputElement) {
          const inputRect = inputElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const chatPageInputPosition = isMobile
            ? viewportHeight - 80
            : viewportHeight - 120;
          const distanceToMove =
            chatPageInputPosition - (inputRect.top + inputRect.height / 2);

          setTermsPosition({
            top: distanceToMove,
            left: 0,
            width: 0,
          });
        }
      }
    }, [isLanding, isAnimating, animationPhase, isMobile]);

    const handleInputClick = () => {
      if (isLanding && !isAnimating) {
        setShowSuggestions(true);
      }
    };

    const handleInputChangeWithSuggestions = (
      event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      if (showSuggestions && !isFromClick) {
        setShowSuggestions(false);
      }

      if (expandedPlaceholder && !isFromClick) {
        setExpandedPlaceholder('');
      }

      if (isFromClick) {
        setIsFromClick(false);
      }

      if (currentPlaceholder !== 'What can I help you find?') {
        setCurrentPlaceholder('What can I help you find?');
      }

      // Auto-resize functionality
      const textarea = event.target;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;

      handleInputChange(event);
    };

    const handleSelectPrompt = (
      suggestionMainText: string,
      suggestionExampleText: string,
      type: string
    ) => {
      // Calculate text width immediately for proper spacing
      if (inputValue.trim() !== suggestionMainText.trim()) {
        const approximateWidth = suggestionMainText.length * 8.5; // More accurate approximation
        setTextWidth(approximateWidth);

        // Different search types require different trailing spaces due to positioning calculation
        // PROPERTY needs 2 spaces, PERSON/PHONE/VEHICLE need 1 space, others need 0
        const trailingSpace = getTrailingSpaceForType(type, true);

        const syntheticEvent = {
          target: { value: `${suggestionMainText}${trailingSpace}` },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        setExpandedPlaceholder(suggestionExampleText);
        setIsFromClick(true);
        handleInputChange(syntheticEvent);

        // Simple focus
        if (inputElementRef.current) {
          inputElementRef.current.focus();
          inputElementRef.current.style.height = 'auto';
          inputElementRef.current.style.height = `${Math.min(
            inputElementRef.current.scrollHeight,
            120
          )}px`;
        }
      }
    };

    const handleCloseSuggestions = () => {
      setShowSuggestions(false);
    };

    const handleChipClick = (type: string) => {
      // Only update if the value is different to prevent adding extra spaces
      const textData =
        PLACEHOLDER_COLLECTION.find(
          (item: { searchType: string }) => item.searchType === type
        ) || PLACEHOLDER_COLLECTION[0];
      if (inputValue.trim() !== textData.chipMainText.trim()) {
        // Calculate text width immediately for proper spacing
        // Different search types require different trailing spaces due to positioning calculation
        const trailingSpace = getTrailingSpaceForType(type, false);
        const approximateWidth = textData.chipMainText.length * 8.5;
        setTextWidth(approximateWidth);

        const syntheticEvent = {
          target: { value: `${textData.chipMainText}${trailingSpace}` },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        setExpandedPlaceholder(textData.chipExampleText);
        setIsFromClick(true);
        handleInputChange(syntheticEvent);
        if (inputElementRef.current) {
          inputElementRef.current.focus();
          inputElementRef.current.style.height = 'auto';
          inputElementRef.current.style.height = `${Math.min(
            inputElementRef.current.scrollHeight,
            120
          )}px`;
        }
      }
    };

    useImperativeHandle(ref, () => ({
      handleChipClick,
    }));

    return (
      <AnimatedInputContainer
        ref={inputRef}
        isLanding={isLanding}
        isAnimating={isAnimating}
        animationPhase={animationPhase}
        termsPosition={termsPosition}
      >
        <AnimatedInputButtonContainer
          isLanding={isLanding}
          isAnimating={isAnimating}
          animationPhase={animationPhase}
          showSuggestions={showSuggestions}
        >
          {expandedPlaceholder && inputValue.trim() !== '' ? (
            <ExpandedPlaceholderContainer
              sx={{
                width: isLanding ? '100%' : 'auto',
                flex: isLanding ? 'none' : 1,
                ...(isAnimating && {
                  flex: 1,
                  minWidth: 0,
                }),
              }}
            >
              <StyledInputChatWithExpandedPlaceholder
                ref={inputElementRef}
                placeholder=""
                value={inputValue}
                autoFocus={isLanding ? true : false}
                onChange={handleInputChangeWithSuggestions}
                onKeyPress={handleKeyPress}
                onClick={handleInputClick}
                disabled={isLoading}
                rows={1}
              />
              <ExpandedPlaceholderText textWidth={textWidth}>
                {expandedPlaceholder}
              </ExpandedPlaceholderText>
              {/* Hidden element to measure text width */}
              <span
                ref={measureRef}
                style={{
                  position: 'absolute',
                  visibility: 'hidden',
                  whiteSpace: 'nowrap',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  fontWeight: 'inherit',
                  letterSpacing: 'inherit',
                  top: '-9999px',
                  left: '-9999px',
                }}
              >
                {inputValue}
              </span>
            </ExpandedPlaceholderContainer>
          ) : (
            <StyledInputChat
              ref={inputElementRef}
              placeholder={currentPlaceholder}
              value={inputValue}
              autoFocus={isLanding ? true : false}
              onChange={handleInputChangeWithSuggestions}
              onKeyPress={handleKeyPress}
              onClick={handleInputClick}
              disabled={isLoading}
              rows={1}
              sx={{
                width: isLanding ? '100%' : 'auto',
                flex: isLanding ? 'none' : 1,
                ...(isAnimating && {
                  flex: 1,
                  minWidth: 0,
                }),
              }}
            />
          )}
          <AnimatedButtonContainer
            isReportOpen={isReportOpen}
            isLanding={isLanding}
            isAnimating={isAnimating}
          >
            <AnimatedSendButton isAnimating={isAnimating}>
              <SendButton
                onClick={handleSendMessage}
                hasInputValue={hasInputValue}
              >
                <ArrowUpwardIcon hasInputValue={hasInputValue} />
              </SendButton>
            </AnimatedSendButton>
          </AnimatedButtonContainer>
        </AnimatedInputButtonContainer>
        {isLanding && (
          <AutosuggestionPrompts
            isVisible={showSuggestions}
            onSelectPrompt={handleSelectPrompt}
            onClose={handleCloseSuggestions}
          />
        )}
        {!isLanding && (
          <Text
            fontSize="13px"
            color="text.secondary"
            textAlign="center"
            mt={2}
          >
            This chat feature is powered by artificial intelligence.{' '}
            <ClickHereTooltip>Click here</ClickHereTooltip> to view our AI usage
            disclaimer
          </Text>
        )}
      </AnimatedInputContainer>
    );
  }
);
